import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  addExamSetWithQuestions,
  docxValidate,
  getExtractDocxQuestion,
  getPatternDetailList,
  getSubExam,
  addPYQExamQuestions
} from "../../../Action/adminPanelAction";
import { useDispatch } from "react-redux";
import PageTitle from "../../../Module/pageTitle";
import Dropdown from "../../../Component/dropdown";
import { Copy, Trash2, Wand2, Zap } from "lucide-react";
import {
  getExamListNew,
  getParentCourseNew,
  getParts,
  uploadPyqPdf,
} from "../../../Action/courseAction";
import {
  showErrorSnackbar,
  showSuccessSnackbar,
} from "../../../Action/snackbarAction";
import "./AICommand.css";
import SubmitExamsetPopup from "./SubmitExamsetPopup";
import moment from "moment";
import jsPDF from "jspdf";

// ─── DOMPurify (lazy sanitizer) ───────────────────────────────────────────────
let _purify = null;
const sanitize = (html) => {
  if (typeof window === "undefined") return html;
  try {
    if (!_purify) {
      const DOMPurify = require("dompurify");
      _purify = DOMPurify;
    }
    return _purify.sanitize(html, { USE_PROFILES: { html: true } });
  } catch {
    return html
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/\bon\w+\s*=\s*["'][^"']*["']/gi, "")
      .replace(/javascript:/gi, "");
  }
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const EXTRACT_TIMEOUT_MS = 15 * 60 * 1000;

// ─── MONTH NAMES ──────────────────────────────────────────────────────────────
// Part Type choices — same list as SubmitExamsetPopup (QuestionExtractor).
const PART_TYPE_OPTIONS = [0, 1, 2, 3, 4, 5];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// ─── JSON PARSER ──────────────────────────────────────────────────────────────
function safeParseJSON(raw) {
  if (!raw || typeof raw !== "string") return null;

  const first = raw.indexOf("{");
  const last = raw.lastIndexOf("}");
  if (first === -1 || last === -1) return null;

  let cleaned = raw.slice(first, last + 1);

  // Fast path: the common case is that `cleaned` is already exactly one
  // well-formed JSON object. Try that directly before falling back to the
  // brace-depth scan below, which is only needed for noisy/concatenated text.
  try {
    const direct = JSON.parse(cleaned);
    if (direct?.questions?.length) return direct;
  } catch (e) {
    // fall through to the scan below
  }

  const objects = [];
  let depth = 0;
  let start = -1;
  // Answer explanations embed LaTeX (e.g. \frac{a}{b}, 10^{-9}), which is
  // full of literal { and } characters inside JSON string values. Those must
  // NOT be counted as structural braces, or the depth count drifts and
  // splits a single valid JSON object into unparsable fragments.
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < cleaned.length; i++) {
    const ch = cleaned[i];

    if (inString) {
      if (escapeNext) {
        escapeNext = false;
      } else if (ch === "\\") {
        escapeNext = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === "{") {
      if (depth === 0) start = i;
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0 && start !== -1) {
        objects.push(cleaned.slice(start, i + 1));
        start = -1;
      }
    }
  }

  const allQuestions = [];
  for (const obj of objects) {
    try {
      const parsed = JSON.parse(obj);
      if (parsed?.questions?.length) {
        allQuestions.push(...parsed.questions);
      }
    } catch (e) { }
  }

  if (allQuestions.length) {
    return { questions: allQuestions };
  }

  return null;
}

// ─── SCALE PATTERN ────────────────────────────────────────────────────────────
function scalePattern(pattern, newTotal) {
  const origTotal = pattern.reduce((s, sec) => s + sec.totalQuestions, 0);
  const ratio = newTotal / (origTotal || newTotal);
  const isBlankPattern = origTotal === 0;
  const totalParts = pattern.reduce((s, sec) => s + sec.parts.length, 0);

  let scaledSections;

  if (isBlankPattern) {
    let remaining = newTotal;
    scaledSections = pattern.map((sec, secIdx) => {
      const isLastSection = secIdx === pattern.length - 1;
      const secShare = isLastSection
        ? remaining
        : Math.floor((sec.parts.length / totalParts) * newTotal);
      remaining -= secShare;
      return { ...sec, scaledTotal: secShare, remainder: 0 };
    });
  } else {
    scaledSections = pattern.map((sec) => ({
      ...sec,
      scaledTotal: Math.floor(sec.totalQuestions * ratio),
      remainder: (sec.totalQuestions * ratio) % 1,
    }));
    let distributed =
      newTotal - scaledSections.reduce((s, sec) => s + sec.scaledTotal, 0);
    scaledSections = scaledSections
      .map((sec, i) => ({ ...sec, i }))
      .sort((a, b) => b.remainder - a.remainder)
      .map((sec, idx) => ({
        ...sec,
        scaledTotal: sec.scaledTotal + (idx < distributed ? 1 : 0),
      }))
      .sort((a, b) => a.i - b.i);
  }

  let cursor = 1;

  return scaledSections.map((sec) => {
    const secTotal = sec.scaledTotal;
    const origSecTotal = sec.totalQuestions;
    const secRatio = secTotal / (origSecTotal || secTotal);
    const isBlankSection = origSecTotal === 0;

    let scaledParts;

    if (isBlankSection) {
      let remainingParts = secTotal;
      scaledParts = sec.parts.map((p, pIdx) => {
        const isLastPart = pIdx === sec.parts.length - 1;
        const partShare = isLastPart
          ? remainingParts
          : Math.floor(secTotal / sec.parts.length);
        remainingParts -= partShare;
        return { ...p, scaledQ: partShare, remainder: 0 };
      });
    } else {
      scaledParts = sec.parts.map((p) => ({
        ...p,
        scaledQ: Math.floor(p.totalQuestions * secRatio),
        remainder: (p.totalQuestions * secRatio) % 1,
      }));
      let partLeftover =
        secTotal - scaledParts.reduce((s, p) => s + p.scaledQ, 0);
      scaledParts = scaledParts
        .map((p, i) => ({ ...p, i }))
        .sort((a, b) => b.remainder - a.remainder)
        .map((p, idx) => ({
          ...p,
          scaledQ: p.scaledQ + (idx < partLeftover ? 1 : 0),
        }))
        .sort((a, b) => a.i - b.i);
    }

    const newParts = scaledParts.map((p) => {
      const from = cursor;
      const to =
        cursor +
        (typeof p.scaledQ === "string" ? parseInt(p.scaledQ, 10) : p.scaledQ) -
        1;
      cursor += p.scaledQ;
      return {
        questionType: p.questionType,
        questionFrom: from,
        questionTo: to,
        totalQuestions: p.scaledQ,
        correct: p.correct,
        negative: p.negative,
        _id: p._id,
        questionTypeName: p.questionTypeName,
      };
    });

    return {
      isCompulsory: sec.isCompulsory,
      name: sec.name,
      totalQuestions: secTotal,
      maxAttempt: secTotal,
      parts: newParts,
      _id: sec._id,
    };
  });
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function PyqQuestionExtraction() {
  const dispatch = useDispatch();

  // ── Multi-file queue ───────────────────────────────────────────────────────
  // Each item: { id, file, year, month, fileType, preview, status, totalQ, manualTotal, questions, logs, fileId }
  const [fileQueue, setFileQueue] = useState([]);
  // Parts fetched per Part Type ({ [partTypeId]: [...] }), shared by all
  // cards so each type is fetched once.
  const [partListsByType, setPartListsByType] = useState({});
  const [activeFileId, setActiveFileId] = useState(null);
  const [isRunningQueue, setIsRunningQueue] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState("preview");

  const fileRef = useRef();
  const abortRef = useRef(null);
  const mathJaxLoaded = useRef(false);
  const imageMapCacheRef = useRef({}); // keyed by queue item id
  const imagePositionsCacheRef = useRef({}); // keyed by queue item id

  // ── Exam / template state ──────────────────────────────────────────────────
  const [aiOutput, setAiOutput] = useState(
    "// AI command output will appear here...",
  );
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [questionTypes, setQuestionTypes] = useState("");
  const [setCount, setSetCount] = useState(1);
  const [exam, setExam] = useState("");
  const [subExam, setSubExam] = useState("");
  const [examStream, setExamStream] = useState("");
  const [subExamList, setSubExamList] = useState([]);
  const [examStreamList, setExamStreamList] = useState([]);
  const [examList, setExamList] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState({
    id: 11,
    name: "PYQ Test",
    icon: "⭕",
    color: "#6366f1",
    type: 4,
  });
  const [patternList, setPatternList] = useState([]);
  const [selectedQuestionTypeList, setSelectedQuestionTypeList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [examSetName, setExamSetName] = useState("");

  // ── Derived values for system prompt ──────────────────────────────────────

  const isSectionWise = activeTemplate?.id === 1;

  let scaledPattern;

  // Builds the "Q<from>–Q<to> → sectionId/partId/marks" lookup table that gets
  // embedded in the system prompt so the model can assign sections deterministically
  // instead of guessing from topic names.
  const buildRangeLookup = (sectionData) => {
    const rows = [];
    sectionData.forEach((section) => {
      (section.parts || []).forEach((part) => {
        rows.push(
          `  Q${part.questionFrom}–Q${part.questionTo}` +
          ` → sectionId="${section._id}"` +
          ` | partId="${part._id}"` +
          ` | marks=${part.correct}` +
          ` | negativeMarks=${part.negative}`,
        );
      });
    });
    return rows.join("\n");
  };

  const fetchSystemPrompt = (item) => {
    const examPatternId = item?.questionTypes?._id ?? "";
    // Prefer the selected exam pattern's own totalQuestions (authoritative),
    // then fall back to a manual override, then a generic default.
    const requestedTotal =
      parseInt(item?.questionTypes?.totalQuestions) ||
      parseInt(item?.manualTotal) ||
      totalQuestions;
    scaledPattern = scalePattern(
      item?.questionTypes?.sectionData || [],
      requestedTotal,
    );

    const SYSTEM_PROMPT_TEXT = `
You are a JSON generator.

Return ONLY valid JSON.
No text before or after JSON.

FORMAT:
{"questions":[...]}

OUTPUT RULE:
- Return ONLY valid JSON
- NO markdown
- NO explanation
- JSON must be complete

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🚫 ABSOLUTE IMAGE URL PROHIBITION — HIGHEST PRIORITY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

NEVER generate or use any URL for images. This includes:
  ❌ https://...
  ❌ http://...
  ❌ s3://...
  ❌ Any domain, path, or file URL string

The ONLY valid image format is:
  { "descType": "img", "description": "<image_placeholder data-key=\\"img_X\\">" }

Where img_X comes from the AVAILABLE IMAGES section below.
ANY URL in your output = INVALID. Reject and use placeholder keys only.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📌 TITLE vs OPTION IMAGE PLACEMENT — CRITICAL RULE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Before placing any image, identify WHERE it appears in the PDF:

CASE 1 — Each option (A/B/C/D) is its own chemical structure / drawing:
  → title  = [ { "descType": "text", "description": "<p>question text</p>" } ]  ← NO image in title
  → Each option gets its OWN img block with a sequential key:
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_0\\">", "isCorrect": false }
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_1\\">", "isCorrect": false }
    ...

    ⚠️ CRITICAL — IF OPTIONS ARE IMAGES IN THE PDF:
Even if the option appears to be a single letter or simple symbol inside a box/shape,
if it is rendered as an IMAGE in the PDF (not plain text), it MUST use descType: "img".
NEVER convert image options to text. If the PDF shows options as drawn boxes, shapes,
or figures — use img blocks regardless of how simple the content looks.

CASE 2 — A single diagram appears in the question body (before the options):
  → title  = [
      { "descType": "text", "description": "<p>question text</p>" },
      { "descType": "img",  "description": "<image_placeholder data-key=\\"img_X\\">" }
    ]
  → options = text-only blocks

CASE 3 — Both a title diagram AND image options exist:
  → Include img block in title AND separate img block per option.

❌ WRONG — option image placed in title:
  "title": [
    { "descType": "text", "description": "<p>Which carbocation is most stable?</p>" },
    { "descType": "img",  "description": "<image_placeholder data-key=\\"img_0\\">" }
  ]

✅ CORRECT — text-only title, each option is its own image:
  "title": [
    { "descType": "text", "description": "<p>Which carbocation is most stable?</p>" }
  ],
  "options": [
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_0\\">", "isCorrect": false },
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_1\\">", "isCorrect": false },
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_2\\">", "isCorrect": true  },
    { "descType": "img", "description": "<image_placeholder data-key=\\"img_3\\">", "isCorrect": false }
  ]

RULES:
1. Return ONLY questions requested in range.
2. "order" must match question number.
3. Each question must follow this structure:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 SECTION ASSIGNMENT — MANDATORY LOOKUP TABLE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

examPatternId = "${examPatternId}"

FOR EVERY QUESTION: find its order number in the table below.
Use the sectionId, partId, marks, and negativeMarks from that row.
This is the ONLY way to assign sections — do NOT use topic names or guessing.

${buildRangeLookup(scaledPattern)}

ASSIGNMENT ALGORITHM:
  1. Look at the question's "order" number.
  2. Find the row where order is between Q_from and Q_to (inclusive).
  3. Copy sectionId, partId, marks, negativeMarks exactly from that row.
  4. Set examPatternId = "${examPatternId}"

VALIDATION:
  ✅ sectionId MUST be a real value from the table above — NEVER ""
  ✅ partId MUST be a real value from the table above — NEVER ""
  ✅ marks MUST equal the correct value from the table — NEVER 0 unless table says 0
  ❌ Do NOT assign a question to a section whose range excludes its order number
  ❌ Do NOT use topic/subject name to decide the section

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📐 QUESTION STRUCTURE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
QUESTION TYPE:
  - questionType 1 = MCQ (Single correct answer)
  - questionType 2 = MSQ (Multiple correct answers)
  - questionType 3 = NAT (Numerical Answer Type)

complexity Level:
  - complexityLevel 1 = EASY
  - complexityLevel 2 = MEDIUM
  - complexityLevel 3 = HARD
{
  "complexityLevel": 1-3,
  "createdByAI": true,
  "unit": "topic",
  "order": number,
  "title": [ ...blocks ],
  "options": [
    { "descType": "text"|"img", "description": "<p>...</p>"|"<image_placeholder data-key=\\"KEY\\">", "isCorrect": true/false }
  ],
  "questionType": 1|2|3,
  "marks": number,
  "negativeMarks": number,
  "ansDescription": { "text": "<p>FULL explanation here — include axiom, deduction, why each wrong option is wrong, exam connection, speed hack, and remember note.</p>", "image": "", "video": "" },
  "decimalPlaces": number,
  "range": { "r1": 0, "r2": null },
  "isPYQ": true,
  "section": {
    "examPatternId": "${examPatternId}",
   "sectionId": "<from lookup table>",
    "partId": "<from lookup table>"
  }
}

─── MULTI-BLOCK RULE (title, options, ansDescription) ───────────────────────

"title" and each option's content may contain MULTIPLE blocks in the order they appear.
Each block is either:
  { "descType": "text", "description": "<p>text here</p>" }
  { "descType": "img",  "description": "<image_placeholder data-key=\\"KEY\\">" }

Rules:
- Preserve the exact order of text and image as they appear in the source.
- If a question title is: [text] → [image] → [text], produce 3 blocks in that order.
- If a question title is text only, produce a single text block.
- If a question title is image only, produce a single image block.
- Apply the same multi-block logic to each option and to ansDescription.text.
- "ansDescription.text" MUST contain the COMPLETE answer explanation. Include ALL of the following:
    • Axiom / principle
    • Step-by-step deduction
    • Why each wrong option is wrong
    • Exam connection
    • Speed hack
    • Remember note

OPTION IMAGE RULE:
If the options in a question are structural/chemical drawings (not text),
each option MUST use descType: "img" — never describe the structure as text.
If multiple options share one combined image, still use individual img blocks
per option using the same or sequential image keys.
NEVER convert image options to text even if the option content is a single letter or number.
If the option is a drawn/rendered image in the PDF → it MUST be descType: "img".
NEVER substitute text descriptions for image options.

MANDATORY FORMAT — use exactly this syntax:
  { "descType": "img", "description": "<image_placeholder data-key=\\"KEY\\">" }

IMAGE INSERTION RULE (CRITICAL):
- If the question text says "based on the chart/graph/diagram/table below"
  OR "refer to the figure/diagram/table below" OR similar →
  you MUST insert an img block in the title AFTER the text block.
- NEVER drop the image reference from the title.
- NEVER remove "Based on..." or "Refer to..." from the question text.
- The text block AND the img block must BOTH appear in the title array.

EXAMPLE — correct title with image:
  "title": [
    { "descType": "text", "description": "<p>Based on the chart shown below, what is X?</p>" },
    { "descType": "img",  "description": "<image_placeholder data-key=\\"KEY\\">" }
  ]

EXAMPLE — title with image between text:
  "title": [
    { "descType": "text", "description": "<p>Consider the circuit shown below:</p>" },
    { "descType": "img",  "description": "<image_placeholder data-key=\\"KEY\\">" },
    { "descType": "text", "description": "<p>What is the current through R1?</p>" }
  ]

EXAMPLE — ansDescription with image:
  "ansDescription": {
    "text": "<p>See the diagram above.</p>",
    "image": "<image_placeholder data-key=\\"KEY\\">",
    "video": ""
  }

━━━━━━━━━━━━━━━━━━━━━━━
📐 EQUATION FORMAT RULES:
━━━━━━━━━━━━━━━━━━━━━━━
- Wrap inline equations with \\( ... \\)  → example: \\(e^{-E/kT}\\)
- Wrap display equations with \\[ ... \\] → example: \\[\\langle E \\rangle = \\frac{E e^{-E/kT}}{Z}\\]
- NEVER write plain-text math like e^(−E/kT) or (M/ℏ)²
- Greek letters: \\alpha \\beta \\varepsilon \\hbar \\theta \\phi
- Fractions: \\frac{numerator}{denominator}
- Subscripts: E_{1}   Superscripts: e^{-\\beta\\varepsilon}

─── SECTION DATA ─────────────────────────────────────────────────────────────

${JSON.stringify(scaledPattern)}

Each section has "_id" (sectionId) and "parts" array. Each part has:
- "_id" (partId)
- "questionType" (1 = MCQ, 2 = MSQ, 3 = NAT)
- "correct" (marks for correct answer)
- "negative" (negative marks for wrong answer)

─── IMPORTANT ────────────────────────────────────────────────────────────────

- "section.sectionId" → "_id" of the matching section
- "section.partId"    → "_id" of the part whose "questionType" matches this question
- "marks"             → value of "correct" from the matching part
- "negativeMarks"     → value of "negative" from the matching part
- MCQ → 4 options, only 1 isCorrect: true
- MSQ → 4 options, multiple isCorrect: true
- NAT → options = []
- Wrap all text in <p></p>
- Use MathJax \\\\(...\\\\)
- Escape backslashes properly (\\\\\\\\)
- decimalPlaces: NAT only (0, 1, 2, or 3); always 0 for MCQ/MSQ
- Section question counts are STRICT and MANDATORY.
- Generate EXACTLY "totalQuestions" for each section.
- Do NOT move questions between sections.
- Respect questionFrom and questionTo ranges strictly.

NAT RULE (STRICT DECISION LOGIC):

- questionType = 3
- options = []

STEP 1: Identify answer type

IF the problem has exact computable value / unique numerical answer / standard formula result:
  r1 = exact value, r2 = null

IF the problem has inequality / tolerance / uncertainty / asks for "range"/"interval"/"between":
  r1 = lower bound, r2 = upper bound

STEP 2: Validation
- Exact answer exists → DO NOT return range
- Range required → r2 MUST be > r1
- Not explicitly required → ALWAYS use fixed value

STEP 3: decimalPlaces — match precision: Integer → 0, Decimal → 1–3

INVALID: returning range when exact answer exists | r1 = 0 and r2 ≠ 0 for NAT
`;
    return SYSTEM_PROMPT_TEXT;
  };

  // ── Exam list init ─────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(
      getExamListNew((res) => {
        setExamList(res);
      }),
    );
  }, []);

  const onChangeExam = (value) => {
    setExam(value);
    setSubExam("");
    setSubExamList([]);
    setExamStream("");
    if (value) {
      dispatch(
        getSubExam({ examTypeId: value._id }, (res) => {
          if (res.data.length > 0) {
            setSubExamList(res.data);
          } else {
            dispatch(
              getParentCourseNew(`${value._id}`, (res) => {
                setExamStreamList(res);
              }),
            );
          }
        }),
      );
    }
  };

  const onChangeSubExam = (value) => {
    setSubExam(value);
    setExamStream("");
    if (value) {
      dispatch(
        getParentCourseNew(`${exam._id}/${value._id}`, (res) => {
          setExamStreamList(res);
        }),
      );
    }
  };

  const onChangeExamStream = (value) => {
    setExamStream(value);
    if (value) {
      const postData = {
        isModelSet: true,
        examTypeId: exam?._id,
        subExamTypeId: subExam?._id,
        baseCourseId: value?._id,
      };
      dispatch(
        getPatternDetailList(postData, (res) => {
          const newArray = [{ _id: 0, name: "Pattern" }, ...res];
          setPatternList(newArray);
        }),
      );
    }
  };

  const getQuestionTypesFromSections = (sections = []) => {
    const types = new Set();
    sections.forEach((section) => {
      section.parts?.forEach((part) => {
        if (part?.questionTypeName) types.add(part.questionTypeName);
      });
    });
    return Array.from(types);
  };

  const onChangeQuestionPattern = (val) => {
    setQuestionTypes(val);
    setSelectedQuestionTypeList(getQuestionTypesFromSections(val?.sectionData));
  };

  // ── API call wrapper ───────────────────────────────────────────────────────
  // Ports the exact call mechanism used by the (working) single-file Question
  // Extractor: the raw file is uploaded directly on every call via
  // getExtractDocxQuestion — there is no separate "upload once, get a file_id"
  // step for the extraction call itself.
  const callAPIPromise = (
    file,
    systemPromptArr,
    promptArr,
    examPatternId,
    signal,
    mode = "extract",
  ) =>
    new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException("Aborted", "AbortError"));
        return;
      }
      const onAbort = () => reject(new DOMException("Aborted", "AbortError"));
      signal?.addEventListener("abort", onAbort, { once: true });

      const payload = {
        examType: 4,
        isFreeUser: false,
        isPracticeTest: false,
        isPYQ: true,
        examPatternId,
        systemPrompt: systemPromptArr,
        prompt: promptArr,
        mode,
      };

      dispatch(
        getExtractDocxQuestion(file, payload, (res) => {
          signal?.removeEventListener("abort", onAbort);
          if (signal?.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
          }
          if (!res) {
            reject(new Error("No response from API"));
            return;
          }

          const content = res?.content ?? res?.data?.content ?? [];
          const textBlock = Array.isArray(content)
            ? content.find((b) => b.type === "text")
            : null;
          const text =
            textBlock?.text ??
            (typeof res?.output === "string" ? res.output : "") ??
            (typeof res === "string" ? res : "");
          const turns = textBlock?.turns ?? res?.turns ?? null;
          const usage = res?.usage ?? res?.data?.usage ?? null;

          if (!text) {
            reject(new Error("API returned empty text"));
            return;
          }
          resolve({ text, turns, usage });
        }),
      );
    });

  // ── Replace image placeholders ─────────────────────────────────────────────
  const replacePlaceholders = (questionsList, imageMap) => {
    if (!Object.keys(imageMap).length) return questionsList;

    const replaceInHtml = (html) => {
      if (!html || typeof html !== "string") return html;
      return html.replace(
        /<image_placeholder[^>]*data-key="([^"]+)"[^>]*\/?>/g,
        (_, key) => {
          const url = imageMap[key];
          return url
            ? `${url}`
            : `<span style="color:#f87171;font-size:11px">[Image: ${key} not found]</span>`;
        },
      );
    };

    const replaceInBlocks = (blocks) =>
      (blocks || []).map((block) => {
        if (block.descType !== "img" && block.descType !== "image")
          return block;
        const keyMatch = block.description?.match(/data-key="([^"]+)"/);
        const key = keyMatch?.[1];
        const url = key ? imageMap[key] : null;
        return {
          ...block,
          description: url
            ? `${url}`
            : `<span style="color:#f87171">[Image not found${key ? `: ${key}` : ""}]</span>`,
        };
      });

    return questionsList.map((q) => ({
      ...q,
      title: replaceInBlocks(q.title),
      options: (q.options || []).map((opt) => ({
        ...opt,
        description:
          opt.descType === "img" || opt.descType === "image"
            ? replaceInBlocks([opt])[0]?.description
            : replaceInHtml(opt.description),
      })),
      ansDescription: {
        ...q.ansDescription,
        text: replaceInHtml(q.ansDescription?.text),
      },
    }));
  };

  // ── Build prompt helpers ───────────────────────────────────────────────────
  const buildSystemPrompt = (text) => [
    { type: "text", text, cache_control: { type: "ephemeral" } },
  ];

  const buildPrompt = (instructionText) => [
    { type: "text", text: instructionText },
  ];

  // ── Queue helpers ──────────────────────────────────────────────────────────
  // Part Type changed on a card: reset its Part and load that type's parts
  // (once per type — same getParts call the Submit popup uses).
  const onChangeItemPartType = (item, value) => {
    updateQueueItem(item.id, { partType: value, partId: "" });
    if (value === "" || partListsByType[value]) return;
    dispatch(
      getParts(value, (res) => {
        setPartListsByType((prev) => ({
          ...prev,
          [value]: Array.isArray(res) ? res : [],
        }));
      }),
    );
  };

  const updateQueueItem = (id, patch) => {
    setFileQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const addItemLog = (id, msg, type = "info") => {
    setFileQueue((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
            ...item,
            logs: [
              ...item.logs,
              { msg, type, time: new Date().toLocaleTimeString() },
            ],
          }
          : item,
      ),
    );
  };

  // ── Add files to queue ─────────────────────────────────────────────────────
  const isDocFile = (f) =>
    f.type === "application/msword" ||
    f.type ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    f.name.toLowerCase().endsWith(".doc") ||
    f.name.toLowerCase().endsWith(".docx");

  const handleFileDrop = (files) => {
    const now = new Date();
    const newItems = Array.from(files)
      .filter(
        (f) =>
          f.type.startsWith("image/") ||
          f.type === "application/pdf" ||
          isDocFile(f),
      )
      .map((f) => ({
        id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        file: f,
        year: now.getFullYear().toString(),
        month: String(now.getMonth() + 1).padStart(2, "0"),
        fileType: f.type.startsWith("image/")
          ? "image"
          : f.type === "application/pdf"
            ? "pdf"
            : "doc",
        preview: URL.createObjectURL(f),
        status: "pending",
        totalQ: "",
        manualTotal: "",
        questions: [],
        logs: [],
        fileId: null,
        questionTypes: {},
        saving: false,
        saved: false,
        // Explicit "PYQ Pdf" reference the admin attaches per-card — separate
        // from `file` (the source used for AI extraction above).
        pyqPdfFile: null,
        // Per-card Part Type (→ partNo) and Part (→ examPartId), same choices
        // as the Submit popup in QuestionExtractor. Both required to save.
        partType: "",
        partId: "",
        // Per-card SOP validation state for docx files.
        validating: false,
        sopValidated: null, // null | "pass" | "fail"
        sopErrors: [],
      }));
    setFileQueue((prev) => [...prev, ...newItems]);
    if (newItems.length > 0 && !activeFileId) {
      setActiveFileId(newItems[0].id);
    }
  };

  const removeFromQueue = (id) => {
    setFileQueue((prev) => prev.filter((item) => item.id !== id));
    if (activeFileId === id) {
      setActiveFileId(null);
    }
    delete imageMapCacheRef.current[id];
    delete imagePositionsCacheRef.current[id];
  };

  // ── Image extraction per item ──────────────────────────────────────────────
  // No separate "upload file → get file_id" step for extraction anymore — the
  // extraction API call uploads the raw file directly (see callAPIPromise).
  // Image placeholder extraction no longer calls a dedicated endpoint (removed
  // along with extract-pdf-images), so this just caches an empty map/positions.
  const ensureImagesExtractedForItem = async (item) => {
    if (imageMapCacheRef.current[item.id]) {
      return {
        imageMap: imageMapCacheRef.current[item.id],
        imagePositions: imagePositionsCacheRef.current[item.id] || {},
      };
    }

    imageMapCacheRef.current[item.id] = {};
    imagePositionsCacheRef.current[item.id] = {};
    return { imageMap: {}, imagePositions: {} };
  };

  // ── Extract a single queue item ────────────────────────────────────────────
  const extractItem = async (item, controller) => {
    // The selected exam pattern's totalQuestions is authoritative when
    // present; manual override comes next. This is only a soft hint for the
    // initial section-pattern lookup table baked into the system prompt —
    // the REAL count comes from the model's response after extraction, and
    // sections/marks get recalculated against that actual count below.
    const requestedTotal =
      parseInt(item?.questionTypes?.totalQuestions) ||
      parseInt(item.manualTotal) ||
      null;

    updateQueueItem(item.id, { status: "uploading", questions: [], logs: [] });

    await ensureImagesExtractedForItem(item);

    updateQueueItem(item.id, { status: "extracting" });

    const examPatternId = item?.questionTypes?._id ?? "";
    const imageMap = imageMapCacheRef.current[item.id] || {};
    const imagePositions = imagePositionsCacheRef.current[item.id] || {};
    const imageKeyList = Object.keys(imageMap);
    const hasImages = imageKeyList.length > 0;

    const positionContext =
      Object.keys(imagePositions).length > 0
        ? `\n\nIMAGE TO QUESTION MAPPING:\n` +
        imageKeyList
          .map((k) => {
            const pos = imagePositions[k];
            return pos?.questionHint
              ? `  Q${pos.questionNumber} ${pos.placement} -> ${k}`
              : `position unknown → ${k}`;
          })
          .join("\n")
        : "";

    const imageContext = hasImages
      ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🚫 ABSOLUTE IMAGE RULE — HIGHEST PRIORITY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Available Keys:
${imageKeyList.map((k) => `  ${k}`).join("\n")}


REQUIRED FORMAT:
{
  "descType": "img",
  "description": "<image_placeholder data-key=\\"KEY\\">"
}

Never use URLs.
Never invent image keys.
Use only keys listed above.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
`
      : `
NO IMAGES: This document contains no extracted images.
Do not generate img blocks.
`;

    const system = buildSystemPrompt(fetchSystemPrompt(item) + imageContext);

    addItemLog(
      item.id,
      requestedTotal
        ? `🚀 Extracting all questions (approx. ${requestedTotal}) in a single request`
        : `🚀 Extracting all questions in a single request`,
      "info",
    );

    const correctSection = (q, pattern) => {
      for (const section of pattern) {
        for (const part of section.parts || []) {
          if (q.order >= part.questionFrom && q.order <= part.questionTo) {
            q.section = {
              examPatternId,
              sectionId: section._id,
              partId: part._id,
            };
            q.marks = part.correct;
            q.negativeMarks = part.negative;
            return q;
          }
        }
      }
      addItemLog(
        item.id,
        `⚠️ Q${q.order}: no matching range — section not assigned`,
        "warn",
      );
      return q;
    };

    const userPrompt = `
${positionContext}
Extract EVERY question in this document — every single one, from start to end.
Do not skip, truncate, or summarize. The exact number of questions is not known in advance;
count and return however many questions actually exist in the document.

OUTPUT RULES:
- STRICT JSON ONLY
- First character MUST be {
- Last character MUST be }
- No markdown
- No explanation
- No text before JSON
- No text after JSON

FORMAT:
{"questions":[...]}

Return ALL questions in one response.`;

    const prompt = buildPrompt(userPrompt);

    const tryOnce = () =>
      new Promise((resolve, reject) => {
        if (controller.signal.aborted) {
          reject(new DOMException("Aborted", "AbortError"));
          return;
        }
        const timer = setTimeout(
          () => reject(new Error(`Timeout after ${EXTRACT_TIMEOUT_MS}ms`)),
          EXTRACT_TIMEOUT_MS,
        );
        const onGlobalAbort = () => {
          clearTimeout(timer);
          reject(new DOMException("Aborted", "AbortError"));
        };
        controller.signal.addEventListener("abort", onGlobalAbort, {
          once: true,
        });

        callAPIPromise(item.file, system, prompt, examPatternId, null, "extract")
          .then((result) => {
            clearTimeout(timer);
            controller.signal.removeEventListener("abort", onGlobalAbort);
            resolve(result);
          })
          .catch((err) => {
            clearTimeout(timer);
            controller.signal.removeEventListener("abort", onGlobalAbort);
            reject(err);
          });
      });

    try {
      const { text } = await tryOnce();
      const first = text.indexOf("{");
      const last = text.lastIndexOf("}");
      if (first === -1 || last === -1)
        throw new Error("No JSON in response");

      const parsed = safeParseJSON(text.slice(first, last + 1));
      if (!parsed?.questions?.length)
        throw new Error("Empty questions array");

      // Dedup by order
      const seen = new Set();
      const uniqueQuestions = parsed.questions.filter((q) => {
        if (seen.has(q.order)) return false;
        seen.add(q.order);
        return true;
      });

      // Rescale the pattern to the ACTUAL extracted count, then assign sections
      const finalPattern = scalePattern(
        item?.questionTypes?.sectionData || [],
        uniqueQuestions.length,
      );
      const correctedQuestions = uniqueQuestions
        .map((q) => correctSection(q, finalPattern))
        .sort((a, b) => a.order - b.order);

      setFileQueue((prev) =>
        prev.map((qi) =>
          qi.id === item.id ? { ...qi, questions: correctedQuestions } : qi,
        ),
      );

      addItemLog(
        item.id,
        `✅ Extracted ${correctedQuestions.length} questions`,
        "success",
      );
      // Immediately surface the results — without this the panel can stay
      // on the Preview tab even though extraction succeeded.
      setActiveTab("questions");
    } catch (e) {
      if (!controller.signal.aborted) {
        addItemLog(item.id, `❌ Extraction failed: ${e.message}`, "error");
      }
    }

    // Final: replace placeholders and update status
    setFileQueue((prev) =>
      prev.map((qi) => {
        if (qi.id !== item.id) return qi;
        const withImages = replacePlaceholders(qi.questions, imageMap);
        return {
          ...qi,
          questions: withImages,
          totalQ: withImages.length,
          manualTotal: String(withImages.length),
          status: controller.signal.aborted ? "failed" : "done",
        };
      }),
    );

    return new Promise((resolve) => {
      setFileQueue((prev) => {
        const qi = prev.find((i) => i.id === item.id);
        if (qi) {
          const finalCount = qi.questions.length;
          addItemLog(
            item.id,
            `🏁 Done. ${finalCount} questions extracted.`,
            "success",
          );
          resolve(controller.signal.aborted ? null : qi);
        } else {
          resolve(null);
        }
        return prev;
      });
    });
  };

  // ── Run entire queue sequentially ──────────────────────────────────────────
  const runQueue = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setIsRunningQueue(true);

    const snapshot = fileQueue.filter(
      (item) => item.status === "pending" || item.status === "failed",
    );

    try {
      for (const item of snapshot) {
        if (controller.signal.aborted) break;
        setActiveFileId(item.id);
        setActiveTab("preview");

        try {
          await extractItem(item, controller);
        } catch (e) {
          // Guarantees a visible error instead of a silent hang if anything
          // inside extractItem throws unexpectedly.
          addItemLog(item.id, `❌ Extraction crashed: ${e.message}`, "error");
          updateQueueItem(item.id, { status: "failed" });
        }

        // Extraction only — saving is now a separate, explicit action the
        // admin triggers per-file from the output panel after reviewing.
        setActiveTab("questions");
      }
    } finally {
      setIsRunningQueue(false);
      loadMathJax();
      typesetMath();
    }
  };

  const cancelQueue = () => {
    abortRef.current?.abort();
    setIsRunningQueue(false);
  };

  // ── Extract only a specific item (manual trigger) ──────────────────────────
  const extractSingleItem = async (item) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setIsRunningQueue(true);
    setActiveFileId(item.id);
    setActiveTab("preview");
    try {
      await extractItem(item, controller);
    } catch (e) {
      addItemLog(item.id, `❌ Extraction crashed: ${e.message}`, "error");
      updateQueueItem(item.id, { status: "failed" });
    } finally {
      setIsRunningQueue(false);
      loadMathJax();
      typesetMath();
    }
  };

  // ── MathJax ───────────────────────────────────────────────────────────────
  const loadMathJax = () => {
    if (mathJaxLoaded.current || document.getElementById("mathjax-script"))
      return;
    mathJaxLoaded.current = true;
    window.MathJax = {
      tex: {
        inlineMath: [
          ["\\(", "\\)"],
          ["$", "$"],
        ],
        displayMath: [
          ["\\[", "\\]"],
          ["$$", "$$"],
        ],
      },
      startup: { typeset: false },
    };
    const sc = document.createElement("script");
    sc.id = "mathjax-script";
    sc.src =
      "https://cdnjs.cloudflare.com/ajax/libs/mathjax/3.2.2/es5/tex-chtml.min.js";
    sc.async = true;
    sc.onload = () => window.MathJax?.typesetPromise?.();
    document.head.appendChild(sc);
  };

  const typesetMath = () => {
    setTimeout(() => {
      if (window.MathJax?.typesetPromise) window.MathJax.typesetPromise();
    }, 300);
  };

  const copyJSON = (questions) =>
    navigator.clipboard.writeText(
      JSON.stringify({ totalExtracted: questions.length, questions }, null, 2),
    );

  const stripHTML = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const loadImage = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = url;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => resolve(null);
    });
  };

  const getFullContent = (docItem) => {
    let questionText = "";
    let questionImages = [];

    docItem.title?.forEach((item) => {
      if (item.descType === "text") {
        questionText += stripHTML(item.description) + " ";
      } else if (item.descType === "img") {
        questionImages.push(item.description);
      }
    });

    let optionsText = "";
    let optionImages = [];

    docItem.options?.forEach((opt, i) => {
      const label = String.fromCharCode(65 + i);

      if (opt.descType === "text") {
        optionsText += `${label}. ${stripHTML(opt.description)}\n`;
      } else if (opt.descType === "img") {
        optionsText += `${label}. [Image]\n`;
        optionImages.push(opt.description);
      }
    });

    let answerImages = [];
    if (docItem.ansDescription?.image) {
      answerImages.push(docItem.ansDescription.image);
    }

    return {
      questionText,
      optionsText,
      questionImages,
      optionImages,
      answerImages,
    };
  };

  const handleExport = async (questionData) => {
    const doc = new jsPDF();
    let startY = 10;

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const checkPageBreak = (requiredHeight = 20) => {
      if (startY + requiredHeight > pageHeight - 10) {
        doc.addPage();
        startY = 10;
      }
    };

    for (let partIndex = 0; partIndex < questionData.length; partIndex++) {
      const part = questionData[partIndex];
      const partName = part.fileName || "";
      const documents = part.questions || [];

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`${partName} (Total: ${documents.length})`, 10, startY);

      startY += 6;

      const imageCache = {};

      for (let docItem of documents) {
        const { questionImages, optionImages, answerImages } =
          getFullContent(docItem);

        const allImages = [...questionImages, ...optionImages, ...answerImages];

        for (let url of allImages) {
          if (url && !imageCache[url]) {
            imageCache[url] = await loadImage(url);
          }
        }
      }

      for (let index = 0; index < documents.length; index++) {
        const q = documents[index];

        checkPageBreak(40);

        doc.setFillColor(240, 240, 240);
        doc.rect(8, startY - 5, 194, 10, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(`Q${index + 1}`, 10, startY + 1);

        doc.setFontSize(9);
        doc.text(
          `Marks: +${q.marks || 0} / -${q.negativeMarks || 0}`,
          160,
          startY + 1,
        );

        startY += 10;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);

        const questionText =
          q.title
            ?.filter((x) => x.descType === "text")
            ?.map((x) => stripHTML(x.description))
            ?.join(" ") || "";

        const splitQuestion = doc.splitTextToSize(questionText, 180);
        doc.text(splitQuestion, 10, startY);
        startY += splitQuestion.length * 6;

        const questionImages =
          q.title
            ?.filter((x) => x.descType === "img")
            ?.map((x) => x.description) || [];

        for (let imgUrl of questionImages) {
          const base64 = imageCache[imgUrl];
          if (base64) {
            checkPageBreak(50);
            doc.addImage(base64, "JPEG", 10, startY, 80, 45);
            startY += 50;
          }
        }

        if (q.questionType !== 3 && q.options?.length) {
          startY += 4;

          q.options.forEach((opt, optIndex) => {
            checkPageBreak(20);

            const optionLabel = String.fromCharCode(65 + optIndex);
            const optionText =
              opt.descType === "text" ? stripHTML(opt.description) : "";

            const splitOption = doc.splitTextToSize(
              `${optionLabel}. ${optionText}`,
              165,
            );

            if (opt.isCorrect) {
              doc.setTextColor(0, 128, 0);
              doc.setFont("helvetica", "bold");
            } else {
              doc.setTextColor(0, 0, 0);
              doc.setFont("helvetica", "normal");
            }

            doc.text(splitOption, 15, startY);
            startY += splitOption.length * 5;

            if (opt.descType === "img") {
              const base64 = imageCache[opt.description];
              if (base64) {
                checkPageBreak(35);
                doc.addImage(base64, "JPEG", 20, startY, 50, 30);
                startY += 35;
              }
            }

            if (opt.isCorrect) {
              doc.setFont("helvetica", "bold");
              doc.text("✓", 185, startY - 3);
              doc.setTextColor(0, 0, 0);
            }
          });
        }

        if (q.questionType === 3 && q.range) {
          checkPageBreak(20);
          doc.setFont("helvetica", "italic");
          doc.text(`Answer Range: ${q.range.r1} to ${q.range.r2}`, 15, startY);
          startY += 8;
        }

        if (q.ansDescription?.text) {
          checkPageBreak(30);
          doc.setFillColor(248, 250, 252);
          doc.rect(10, startY - 3, 185, 8, "F");

          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text("Explanation", 12, startY + 2);
          startY += 10;

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);

          const explanation = stripHTML(q.ansDescription.text);
          const splitExplanation = doc.splitTextToSize(explanation, 180);
          doc.text(splitExplanation, 12, startY);
          startY += splitExplanation.length * 5;
        }

        startY += 8;
        doc.setDrawColor(220, 220, 220);
        doc.line(10, startY, pageWidth - 10, startY);
        startY += 10;
      }

      if (startY > 260 && partIndex !== questionData.length - 1) {
        doc.addPage();
        startY = 10;
      }
    }

    doc.save(`${questionData[0].fileName}.pdf`);
  };

  // ── Active file item (for right panel) ────────────────────────────────────
  const activeItem =
    fileQueue.find((i) => i.id === activeFileId) ?? fileQueue[0] ?? null;
  const activeQuestions = activeItem?.questions ?? [];

  function formatFileName(name) {
    return name
      .replace(/\.(pdf|docx?|)$/i, "")
      .replace(/[_-]+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function sanitizeForJSON(obj) {
    if (obj === null || obj === undefined) return obj;
    if (obj instanceof Element || obj instanceof Node) return null;
    if (typeof obj !== "object") return obj;
    if (Array.isArray(obj)) return obj.map(sanitizeForJSON);

    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => !(v instanceof Element) && !(v instanceof Node))
        .map(([k, v]) => [k, sanitizeForJSON(v)]),
    );
  }

  /**************Re order question base on section******************* */
  function reorderQuestions(questions, sections = []) {
    const sectionOrderMap = {};
    (sections || []).forEach((section, sectionIndex) => {
      sectionOrderMap[section._id] = sectionIndex;
    });

    const sorted = [...questions].sort((a, b) => {
      const sectionA = sectionOrderMap[a.section?.sectionId] ?? 999;
      const sectionB = sectionOrderMap[b.section?.sectionId] ?? 999;

      if (sectionA !== sectionB) return sectionA - sectionB;
      return a.order - b.order;
    });

    return sorted.map((q, index) => ({
      ...q,
      order: index + 1,
    }));
  }

  // ── Save examset ───────────────────────────────────────────────────────────
  const saveExamset = (completedItem) => {
    return new Promise((resolve, reject) => {
      const buildAndSave = (pyqpdf) => {
      const isPracticeTest = false;
      const duration =
        completedItem?.questionTypes?.duration || completedItem?.totalQ * 1.5;

      const totalMarksVal = completedItem?.questionTypes?.totalMarks;
      const reorderedQuestions = reorderQuestions(
        completedItem.questions,
        completedItem?.questionTypes?.sectionData,
      );

      const examSetObj = {
        order: 1,
        examType: 4,
        isFreeUser: false,
        isPracticeTest,
        isPYQ: true,
        isSectionWise: completedItem?.questionTypes?.isModelSet,
        getAdmitCard: false,
        isActive: true,
        examDate: moment().format(),
        createdByAI: true,
        duration,
        totalQuestions: completedItem?.totalQ,
        totalAddedQuestion: completedItem?.totalQ,
        examPatternId: completedItem?.questionTypes?._id,
        name: formatFileName(completedItem?.file?.name),
        totalMarks: totalMarksVal,
        QuestionData: reorderedQuestions,
        sections: completedItem?.questionTypes?.sectionData,
        examYear: completedItem?.year,
        examMonth: completedItem?.month,
        isPart: true,
        partNo: Number(completedItem?.partType),
        examPartId: completedItem?.partId,
        ...(pyqpdf ? { pyqpdf } : {}),
      };

      const cleaned = sanitizeForJSON(examSetObj);

      let parsed;
      parsed = [
        {
          ...cleaned,
          baseCourseId: examStream?._id,
          examTypeId: exam?._id,
          ...(subExam ? { subExamTypeId: subExam?._id } : {}),
        },
      ];
      dispatch(
        addExamSetWithQuestions({ examSetData: parsed }, (res) => {
          // Matches Question Extraction's saveExamset exactly: the callback
          // firing at all means the dispatch completed — no success/failure
          // shape validation. This action doesn't follow a {success:true/false}
          // contract; it just returns the saved data (which is why it looked
          // like an "echo" of the request — it's the saved records, not an error).

          const payload = [{
            "order": 1,
            "examType": 4,
            "isFreeUser": false,
            "isPracticeTest": false,
            "isPYQ": true,
            "isSectionWise": false,
            "getAdmitCard": false,
            "isActive": true,
            "createdByAI": true,
            "examPatternId": completedItem?.questionTypes?._id,
            "examYear": completedItem?.year,
            "examMonth": completedItem?.month,
            "isPart": true,
            "partNo": Number(completedItem?.partType),
            "examPartId": completedItem?.partId,
            "baseCourseId": examStream?._id,
            "examTypeId": exam?._id,
          ...(subExam ? { subExamTypeId: subExam?._id } : {}),
            "examId": res[0]?._id
          }]
          dispatch(
            addPYQExamQuestions(payload, (res) => {
              // Matches Question Extraction's saveExamset exactly: the callback
              // firing at all means the dispatch completed — no success/failure
              // shape validation. This action doesn't follow a {success:true/false}
              // contract; it just returns the saved data (which is why it looked
              // like an "echo" of the request — it's the saved records, not an error).
              console.log("ExamSet saved:", res);

              resolve(res);
            }),
          );
          // resolve(res);
        }),
      );
      };

      // Upload the explicit "PYQ Pdf" the admin attached on this card (same
      // action/endpoint createExamSet.js uses), then attach the returned
      // reference to the exam set payload before saving. This is separate
      // from `file` (the source used for AI extraction) — if the admin
      // didn't attach one, save proceeds without a pyqpdf field, matching
      // createExamSet.js's own optional/deferred behaviour.
      if (completedItem?.pyqPdfFile) {
        dispatch(
          uploadPyqPdf(completedItem.pyqPdfFile, (resPdf) => {
            buildAndSave({
              file: resPdf?.file,
              originalName:
                resPdf?.originalName || completedItem.pyqPdfFile.name,
            });
          }),
        );
      } else {
        buildAndSave(null);
      }
    });
  };

  // ── Save a reviewed item's extracted questions (explicit, per-item) ────────
  const handleSaveItem = async (item) => {
    if (!item || item.saving || item.saved) return;
    if (!item.questions || item.questions.length === 0) {
      dispatch(showErrorSnackbar("Nothing to save — extract this file first."));
      return;
    }
    if (item.partType === "" || !item.partId) {
      dispatch(showErrorSnackbar("Select Part Type and Part for this file before saving."));
      return;
    }

    updateQueueItem(item.id, { saving: true });
    try {
      await saveExamset(item);
      updateQueueItem(item.id, { saving: false, saved: true });
      addItemLog(item.id, "💾 Saved successfully", "success");
      dispatch(showSuccessSnackbar("PYQ saved successfully"));
    } catch (e) {
      updateQueueItem(item.id, { saving: false });
      addItemLog(item.id, `❌ Save failed: ${e.message}`, "error");
      dispatch(showErrorSnackbar(`Save failed: ${e.message}`));
    }
  };

  // ── Validate a docx file against SOP rules (per-card, manual trigger) ─────
  const validateItemDocx = (item) => {
    if (!item || item.validating) return;

    updateQueueItem(item.id, { validating: true });
    addItemLog(item.id, "🔍 Validating document against SOP…", "info");

    dispatch(
      docxValidate(
        item.file,
        (res) => {
          const errorIssues = (res?.data?.issues || []).filter(
            (issue) => issue.severity === "error",
          );

          if (res?.data?.ok) {
            updateQueueItem(item.id, {
              validating: false,
              sopValidated: "pass",
              sopErrors: [],
            });
            addItemLog(item.id, "✅ SOP validation passed", "success");
          } else {
            updateQueueItem(item.id, {
              validating: false,
              sopValidated: "fail",
              sopErrors:
                errorIssues.length > 0
                  ? errorIssues
                  : [{ message: "Document failed SOP validation.", fix: "" }],
            });
            addItemLog(item.id, "❌ SOP validation failed", "error");
          }
        },
        (err) => {
          updateQueueItem(item.id, {
            validating: false,
            sopValidated: "fail",
            sopErrors: [
              {
                message:
                  err?.msg ||
                  err?.data?.msg ||
                  "Document failed SOP validation",
                fix: "",
              },
            ],
          });
          addItemLog(item.id, "❌ SOP validation failed", "error");
        },
      ),
    );
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const s = {
    page: {
      fontFamily: "Inter,sans-serif",
      maxWidth: 1000,
      margin: "0 auto",
      padding: 10,
      background: "#0f172a",
      minHeight: "400px",
      color: "#e2e8f0",
    },
    drop: {
      border: `2px dashed ${dragOver ? "#6366f1" : "#334155"}`,
      borderRadius: 12,
      padding: 24,
      textAlign: "center",
      cursor: "pointer",
      background: dragOver ? "#1e1b4b" : "#1e293b",
      transition: "all 0.2s",
    },
    card: {
      background: "#1e293b",
      borderRadius: 10,
      padding: 16,
      marginTop: 14,
      border: "1px solid #334155",
    },
    btn: (c, dis) => ({
      padding: "7px 14px",
      borderRadius: 8,
      border: "none",
      cursor: dis ? "not-allowed" : "pointer",
      fontWeight: 600,
      fontSize: 12,
      background: dis ? "#1e293b" : c,
      color: dis ? "#475569" : "#fff",
      transition: "all 0.2s",
    }),
    btnSm: (c, dis) => ({
      padding: "4px 10px",
      borderRadius: 6,
      border: "none",
      cursor: dis ? "not-allowed" : "pointer",
      fontWeight: 600,
      fontSize: 11,
      background: dis ? "#1e293b" : c,
      color: dis ? "#475569" : "#fff",
    }),
    inp: {
      background: "#0f172a",
      border: "1px solid #334155",
      borderRadius: 8,
      padding: "5px 8px",
      color: "#e2e8f0",
      fontSize: 13,
    },
    inpSm: {
      background: "#0f172a",
      border: "1px solid #334155",
      borderRadius: 6,
      padding: "4px 6px",
      color: "#e2e8f0",
      fontSize: 12,
      width: 64,
      textAlign: "center",
    },
    badge: (bg, col) => ({
      fontSize: 11,
      padding: "2px 7px",
      borderRadius: 99,
      fontWeight: 600,
      background: bg,
      color: col,
      whiteSpace: "nowrap",
    }),
    tab: (a) => ({
      padding: "6px 12px",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 500,
      borderRadius: "6px 6px 0 0",
      border: "none",
      background: a ? "#6366f1" : "transparent",
      color: a ? "#fff" : "#94a3b8",
    }),
    opt: (ok) => ({
      display: "flex",
      gap: 8,
      padding: "6px 10px",
      borderRadius: 6,
      marginTop: 4,
      background: ok ? "#064e3b" : "#0f172a",
      border: `1px solid ${ok ? "#10b981" : "#1e293b"}`,
      alignItems: "flex-start",
    }),
    logBox: {
      background: "#0f172a",
      borderRadius: 8,
      padding: 10,
      maxHeight: 150,
      overflowY: "auto",
      fontFamily: "monospace",
      fontSize: 11,
      marginTop: 6,
    },
    logColors: {
      info: "#94a3b8",
      success: "#34d399",
      warn: "#fbbf24",
      error: "#f87171",
    },
    queueItem: (isActive, status) => ({
      background: isActive ? "#1e1b4b" : "#1e293b",
      borderRadius: 10,
      padding: "10px 14px",
      marginTop: 10,
      border: `1px solid ${isActive ? "#6366f1" : status === "done" ? "#10b981" : status === "failed" ? "#ef4444" : "#334155"}`,
      transition: "all 0.2s",
    }),
  };

  const statusBadge = (status, count) => {
    const map = {
      pending: [s.badge("#1e3a5f", "#60a5fa"), "⏳ Pending"],
      uploading: [s.badge("#312e81", "#a5b4fc"), "📤 Uploading"],
      extracting: [s.badge("#78350f", "#fbbf24"), "⚡ Extracting"],
      done: [s.badge("#064e3b", "#34d399"), `✅ Done (${count}q)`],
      failed: [s.badge("#4c0519", "#f87171"), "❌ Failed"],
    };
    const [style, label] = map[status] ?? [
      s.badge("#1e293b", "#94a3b8"),
      status,
    ];
    return <span style={style}>{label}</span>;
  };

  const fileIcon = (fileType) =>
    fileType === "pdf" ? "📕" : fileType === "doc" ? "📄" : "🖼️";

  const pendingCount = fileQueue.filter(
    (i) => i.status === "pending" || i.status === "failed",
  ).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="contentBox">
        <PageTitle name={"PYQ Question Extraction"} />
        <div className="formDiv contentScrollbar">
          <div className="ai-command-center-container">
            <div className="command-content row mx-0">
              {/* ── LEFT SECTION ── */}
              <div className="Ai-left-section col-md-6">
                {/* Quick Command Templates */}
                <div className="quick-templates-card">
                  <div className="card-header-Ai">
                    <Zap size={18} className="header-icon" />
                    <h2>Quick Command Templates</h2>
                  </div>
                  <div className="p-3">
                    <div className="form-outer-AI">
                      <div className="form-field-AI">
                        <label>EXAM *</label>
                        <Dropdown
                          labelName="Exam Name"
                          setInputText={onChangeExam}
                          options={examList}
                          value={exam}
                        />
                      </div>
                      {subExamList?.length > 0 && (
                        <div className="form-field-AI">
                          <label>SUB EXAM *</label>
                          <Dropdown
                            labelName="Sub Exam"
                            setInputText={onChangeSubExam}
                            options={subExamList}
                            value={subExam}
                          />
                        </div>
                      )}
                      <div className="form-field-AI">
                        <label>EXAM STREAM *</label>
                        <Dropdown
                          labelName="Exam Stream"
                          setInputText={onChangeExamStream}
                          options={examStreamList}
                          value={examStream}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Command — Multi-file upload */}
                <div className="ai-command-card position-relative">
                  <div className="card-header-Ai">
                    <div className="header-left">
                      <Wand2 size={18} className="header-icon" />
                      <h2>AI Command — Multi-File Queue</h2>
                    </div>
                    <button className="knowledge-base-btn">
                      Uses Knowledge Base
                    </button>
                  </div>

                  <div className="command-input-section p-1">
                    <div style={s.page}>
                      {/* Drop Zone */}
                      <div
                        style={s.drop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          handleFileDrop(e.dataTransfer.files);
                        }}
                        onClick={() => fileRef.current.click()}
                      >
                        <div style={{ fontSize: 28 }}>📂</div>
                        <p
                          style={{
                            color: "#94a3b8",
                            fontSize: 13,
                            margin: "6px 0 2px",
                          }}
                        >
                          Drag & drop multiple PDFs / DOCs / images or click to
                          add
                        </p>
                        <p
                          style={{ color: "#475569", fontSize: 11, margin: 0 }}
                        >
                          Supports multiple files — extracts one by one in order
                        </p>
                        <input
                          ref={fileRef}
                          type="file"
                          accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => handleFileDrop(e.target.files)}
                        />
                      </div>

                      {/* Queue list */}
                      {fileQueue.map((item) => (
                        <div
                          key={item.id}
                          style={s.queueItem(
                            activeFileId === item.id,
                            item.status,
                          )}
                        >
                          {/* Row 1: filename + status + remove */}
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexWrap: "wrap",
                              marginBottom: 8,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 12,
                                color: "#e2e8f0",
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {fileIcon(item.fileType)} {item.file.name}
                            </span>
                            {statusBadge(item.status, item.questions.length)}
                            {item.sopValidated === "pass" && (
                              <span style={s.badge("#064e3b", "#34d399")}>
                                ✅ SOP OK
                              </span>
                            )}
                            {item.sopValidated === "fail" && (
                              <span style={s.badge("#4c0519", "#f87171")}>
                                ❌ SOP Failed
                              </span>
                            )}
                            {item.status !== "extracting" &&
                              item.status !== "uploading" && (
                                <button
                                  style={s.btnSm("#4c0519", false)}
                                  onClick={() => removeFromQueue(item.id)}
                                >
                                  ✕
                                </button>
                              )}
                          </div>

                          {/* Row 2: pattern / year / month / total / count / extract */}
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Pattern
                              </span>
                              <select
                                style={{ ...s.inpSm, width: 60 }}
                                value={item.questionTypes?._id}
                                onChange={(e) => {
                                  const selected = patternList.find(
                                    (p) => p._id === e.target.value,
                                  );
                                  updateQueueItem(item.id, {
                                    questionTypes: selected,
                                    // Auto-fill Total Q from the pattern's own
                                    // totalQuestions — still editable/overridable.
                                    manualTotal: selected?.totalQuestions
                                      ? String(selected.totalQuestions)
                                      : item.manualTotal,
                                  });
                                }}
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              >
                                {patternList.map((p, i) => (
                                  <option key={i} value={p?._id}>
                                    {p?.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Year
                              </span>
                              <input
                                style={s.inpSm}
                                type="number"
                                min={2000}
                                max={2099}
                                value={item.year}
                                onChange={(e) =>
                                  updateQueueItem(item.id, {
                                    year: e.target.value,
                                  })
                                }
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              />
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Month
                              </span>
                              <select
                                style={{ ...s.inpSm, width: 60 }}
                                value={item.month}
                                onChange={(e) =>
                                  updateQueueItem(item.id, {
                                    month: e.target.value,
                                  })
                                }
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              >
                                {MONTH_NAMES.map((name, i) => {
                                  const val = String(i + 1).padStart(2, "0");
                                  return (
                                    <option key={val} value={val}>
                                      {name}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                            {/* Part Type → partNo, Part → examPartId (both
                                required before Save; sent with isPart: true). */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Part Type *
                              </span>
                              <select
                                style={{ ...s.inpSm, width: 60 }}
                                value={item.partType}
                                onChange={(e) =>
                                  onChangeItemPartType(item, e.target.value)
                                }
                                disabled={
                                  item.saved ||
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              >
                                <option value="">--</option>
                                {PART_TYPE_OPTIONS.map((n) => (
                                  <option key={n} value={String(n)}>
                                    {n}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Part *
                              </span>
                              <select
                                style={{ ...s.inpSm, width: 140 }}
                                value={item.partId}
                                onChange={(e) =>
                                  updateQueueItem(item.id, {
                                    partId: e.target.value,
                                  })
                                }
                                disabled={
                                  item.saved ||
                                  item.partType === "" ||
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              >
                                <option value="">
                                  {item.partType === "" ? "Select type first" : "Select Part"}
                                </option>
                                {(partListsByType[item.partType] || []).map((p) => (
                                  <option key={p?._id} value={p?._id}>
                                    {p?.en?.name || p?.name || ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                Total Q
                              </span>
                              <input
                                style={s.inpSm}
                                type="number"
                                min={1}
                                max={500}
                                placeholder="from pattern"
                                value={item.manualTotal}
                                onChange={(e) =>
                                  updateQueueItem(item.id, {
                                    manualTotal: e.target.value,
                                  })
                                }
                                disabled={
                                  item.status === "extracting" ||
                                  item.status === "uploading"
                                }
                              />
                            </div>

                            {/* Explicit PYQ Pdf attachment — separate from the
                                source file used for AI extraction above. */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                PYQ Pdf
                              </span>
                              <label
                                style={{
                                  ...s.btnSm(
                                    "#334155",
                                    item.status === "extracting" ||
                                      item.status === "uploading",
                                  ),
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  maxWidth: 140,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  margin: 0,
                                }}
                                title={
                                  item.pyqPdfFile?.name || "Upload PYQ Pdf"
                                }
                              >
                                📄{" "}
                                {item.pyqPdfFile
                                  ? item.pyqPdfFile.name
                                  : "Upload PDF"}
                                <input
                                  type="file"
                                  accept=".pdf,application/pdf"
                                  style={{ display: "none" }}
                                  disabled={
                                    item.status === "extracting" ||
                                    item.status === "uploading"
                                  }
                                  onClick={(e) => (e.target.value = null)}
                                  onChange={(e) => {
                                    const pdfFile = e.target.files?.[0];
                                    if (!pdfFile) return;
                                    if (
                                      pdfFile.type !== "application/pdf" &&
                                      !pdfFile.name
                                        .toLowerCase()
                                        .endsWith(".pdf")
                                    ) {
                                      dispatch(
                                        showErrorSnackbar(
                                          "Only PDF files are allowed.",
                                        ),
                                      );
                                      return;
                                    }
                                    updateQueueItem(item.id, {
                                      pyqPdfFile: pdfFile,
                                    });
                                  }}
                                />
                              </label>
                              {item.pyqPdfFile && (
                                <button
                                  style={{
                                    ...s.btnSm("#7f1d1d", false),
                                    padding: "4px 6px",
                                  }}
                                  title="Remove PDF"
                                  onClick={() =>
                                    updateQueueItem(item.id, {
                                      pyqPdfFile: null,
                                    })
                                  }
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            <div
                              style={{
                                marginLeft: "auto",
                                display: "flex",
                                gap: 6,
                              }}
                            >
                              {/* Validate docx against SOP rules */}
                              {item.fileType === "doc" && (
                                <button
                                  style={s.btnSm(
                                    "#0891b2",
                                    item.validating || isRunningQueue,
                                  )}
                                  disabled={item.validating || isRunningQueue}
                                  onClick={() => validateItemDocx(item)}
                                >
                                  {item.validating
                                    ? "🔍 Validating…"
                                    : "🔍 Validate"}
                                </button>
                              )}

                              {/* Individual extract button */}
                              {item.status !== "done" && (
                                <button
                                  style={s.btnSm("#6366f1", isRunningQueue)}
                                  disabled={isRunningQueue}
                                  onClick={() => extractSingleItem(item)}
                                >
                                  ▶ Extract
                                </button>
                              )}

                              {/* View button */}
                              <button
                                style={s.btnSm(
                                  activeFileId === item.id
                                    ? "#4f46e5"
                                    : "#334155",
                                  false,
                                )}
                                onClick={() => {
                                  setActiveFileId(item.id);
                                  setActiveTab(
                                    item.questions.length > 0
                                      ? "questions"
                                      : "preview",
                                  );
                                }}
                              >
                                👁 View
                              </button>
                            </div>
                          </div>

                          {/* Extracting spinner */}
                          {item.status === "extracting" && (
                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 14,
                                  height: 14,
                                  border: "2px solid #334155",
                                  borderTop: "2px solid #6366f1",
                                  borderRadius: "50%",
                                  animation: "spin 1s linear infinite",
                                  flexShrink: 0,
                                }}
                              />
                              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                Extracting all questions in a single request...
                              </span>
                              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </div>
                          )}

                          {/* SOP validation errors */}
                          {item.sopValidated === "fail" &&
                            item.sopErrors.length > 0 && (
                              <div
                                style={{
                                  marginTop: 8,
                                  padding: 8,
                                  borderRadius: 6,
                                  background: "#4c0519",
                                  border: "1px solid #7f1d1d",
                                }}
                              >
                                {item.sopErrors.map((issue, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      fontSize: 11,
                                      color: "#fecaca",
                                      marginBottom:
                                        i === item.sopErrors.length - 1
                                          ? 0
                                          : 4,
                                    }}
                                  >
                                    • {issue.message}
                                    {issue.fix ? ` — ${issue.fix}` : ""}
                                  </div>
                                ))}
                              </div>
                            )}

                          {/* Per-item logs */}
                          {item.logs.length > 0 && (
                            <details style={{ marginTop: 6 }}>
                              <summary
                                style={{
                                  fontSize: 11,
                                  color: "#6366f1",
                                  cursor: "pointer",
                                }}
                              >
                                📋 Logs ({item.logs.length})
                              </summary>
                              <div style={s.logBox}>
                                {item.logs.map((l, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      color: s.logColors[l.type],
                                      marginBottom: 2,
                                    }}
                                  >
                                    <span style={{ color: "#475569" }}>
                                      [{l.time}]
                                    </span>{" "}
                                    {l.msg}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      ))}

                      {/* Queue controls */}
                      {fileQueue.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          {isRunningQueue ? (
                            <button
                              style={s.btn("#ef4444", false)}
                              onClick={cancelQueue}
                            >
                              🛑 Stop Queue
                            </button>
                          ) : (
                            <button
                              style={s.btn("#6366f1", pendingCount === 0)}
                              onClick={runQueue}
                              disabled={pendingCount === 0}
                            >
                              🚀 Extract All Pending ({pendingCount})
                            </button>
                          )}
                          <button
                            style={s.btn("#475569", isRunningQueue)}
                            onClick={() => {
                              setFileQueue([]);
                              setActiveFileId(null);
                              imageMapCacheRef.current = {};
                              imagePositionsCacheRef.current = {};
                            }}
                            disabled={isRunningQueue}
                          >
                            🗑 Clear All
                          </button>
                          <span
                            style={{
                              fontSize: 12,
                              color: "#64748b",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {
                              fileQueue.filter((i) => i.status === "done")
                                .length
                            }
                            /{fileQueue.length} done
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT SECTION ── */}
              <div className="Ai-right-section col-md-6">
                <div className="output-card position-relative">
                  <div className="output-header">
                    <div className="output-title">
                      <span className="output-dot">●</span>
                      <h2>AI Planning Output</h2>
                    </div>
                  </div>

                  <div className="ai-container">
                    <div
                      className="output-content question-output"
                      style={{
                        fontFamily: "monospace",
                        fontSize: 13,
                        color: "#e2e8f0",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {/* File selector tabs (for files with content) */}
                      {fileQueue.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            gap: 4,
                            flexWrap: "wrap",
                            padding: "8px 8px 0",
                            borderBottom: "1px solid #1e293b",
                            overflowX: "auto",
                          }}
                        >
                          {fileQueue.map((item) => (
                            <button
                              key={item.id}
                              style={{
                                padding: "5px 10px",
                                cursor: "pointer",
                                fontSize: 11,
                                fontWeight: 500,
                                borderRadius: "6px 6px 0 0",
                                border: `1px solid ${activeFileId === item.id ? "#6366f1" : "#334155"}`,
                                borderBottom: "none",
                                background:
                                  activeFileId === item.id
                                    ? "#6366f1"
                                    : "#1e293b",
                                color:
                                  activeFileId === item.id ? "#fff" : "#94a3b8",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => {
                                setActiveFileId(item.id);
                                setActiveTab(
                                  item.questions.length > 0
                                    ? "questions"
                                    : "preview",
                                );
                              }}
                            >
                              {item.year}/{item.month}
                              {item.status === "extracting" && " ⚡"}
                              {item.status === "done" &&
                                ` ✅ ${item.questions.length}q`}
                              {item.status === "failed" && " ❌"}
                            </button>
                          ))}
                        </div>
                      )}

                      {activeItem && (
                        <>
                          {/* Content tabs */}
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              borderBottom: "1px solid #1e293b",
                              padding: "0 4px",
                            }}
                          >
                            {activeItem.preview && (
                              <button
                                style={s.tab(activeTab === "preview")}
                                onClick={() => setActiveTab("preview")}
                              >
                                📷 Preview
                              </button>
                            )}
                            {activeQuestions.length > 0 && (
                              <button
                                style={s.tab(activeTab === "questions")}
                                onClick={() => setActiveTab("questions")}
                              >
                                ❓ Questions ({activeQuestions.length})
                              </button>
                            )}
                            {activeQuestions.length > 0 && (
                              <button
                                style={s.tab(activeTab === "json")}
                                onClick={() => setActiveTab("json")}
                              >
                                {"{ }"} JSON
                              </button>
                            )}
                            {/* File info */}
                            <div
                              style={{
                                marginLeft: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "0 8px",
                              }}
                            >
                              <span style={{ fontSize: 11, color: "#64748b" }}>
                                {activeItem.file.name} · {activeItem.year}/
                                {activeItem.month}
                              </span>
                            </div>
                          </div>

                          {/* Preview tab */}
                          {activeTab === "preview" && activeItem.preview && (
                            <div style={{ marginTop: 12, padding: "0 8px" }}>
                              {activeItem.fileType === "image" ? (
                                <img
                                  src={activeItem.preview}
                                  alt="preview"
                                  style={{
                                    maxWidth: "100%",
                                    borderRadius: 8,
                                    border: "1px solid #334155",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    ...s.card,
                                    textAlign: "center",
                                    color: "#94a3b8",
                                  }}
                                >
                                  <div
                                    style={{ fontSize: 40, marginBottom: 8 }}
                                  >
                                    {fileIcon(activeItem.fileType)}
                                  </div>
                                  <strong
                                    style={{
                                      color: "#a5b4fc",
                                      display: "block",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {activeItem.file.name}
                                  </strong>
                                  <div
                                    style={{ fontSize: 12, color: "#64748b" }}
                                  >
                                    {(activeItem.file.size / 1024).toFixed(1)}{" "}
                                    KB ·{activeItem.year}/{activeItem.month}
                                  </div>
                                  {imageMapCacheRef.current[activeItem.id] && (
                                    <span
                                      style={{
                                        ...s.badge("#064e3b", "#34d399"),
                                        marginTop: 8,
                                        display: "inline-block",
                                      }}
                                    >
                                      ✅{" "}
                                      {Object.keys(
                                        imageMapCacheRef.current[
                                        activeItem.id
                                        ] || {},
                                      ).length > 0
                                        ? `${Object.keys(imageMapCacheRef.current[activeItem.id]).length} images found`
                                        : "Images checked"}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Questions tab */}
                          {activeTab === "questions" && (
                            <div
                              style={{ marginTop: 12, padding: "0 8px" }}
                              ref={(el) => {
                                if (el) typesetMath();
                              }}
                            >
                              {/* Stats */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginBottom: 12,
                                }}
                              >
                                {[
                                  ["Total", activeQuestions.length, "#a5b4fc"],
                                  [
                                    "Easy",
                                    activeQuestions.filter(
                                      (q) => q.complexityLevel === 1,
                                    ).length,
                                    "#34d399",
                                  ],
                                  [
                                    "Medium",
                                    activeQuestions.filter(
                                      (q) => q.complexityLevel === 2,
                                    ).length,
                                    "#fbbf24",
                                  ],
                                  [
                                    "Hard",
                                    activeQuestions.filter(
                                      (q) => q.complexityLevel === 3,
                                    ).length,
                                    "#f87171",
                                  ],
                                  [
                                    "MCQ",
                                    activeQuestions.filter(
                                      (q) => q.questionType === 1,
                                    ).length,
                                    "#818cf8",
                                  ],
                                  [
                                    "MSQ",
                                    activeQuestions.filter(
                                      (q) => q.questionType === 2,
                                    ).length,
                                    "#c084fc",
                                  ],
                                  [
                                    "NAT",
                                    activeQuestions.filter(
                                      (q) => q.questionType === 3,
                                    ).length,
                                    "#38bdf8",
                                  ],
                                ].map(([label, val, col]) => (
                                  <div
                                    key={label}
                                    style={{
                                      background: "#1e293b",
                                      borderRadius: 8,
                                      padding: "8px 14px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 18,
                                        fontWeight: 700,
                                        color: col,
                                        display: "block",
                                      }}
                                    >
                                      {val}
                                    </span>
                                    <span
                                      style={{ fontSize: 11, color: "#64748b" }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* Question cards */}
                              {activeQuestions.map((q, i) => {
                                const complexityStyle = {
                                  1: ["#14532d", "#86efac"],
                                  2: ["#78350f", "#fcd34d"],
                                  3: ["#7f1d1d", "#fca5a5"],
                                };
                                const complexityLabel = {
                                  1: "Easy",
                                  2: "Medium",
                                  3: "Hard",
                                };
                                const qtypeLabel = {
                                  1: "MCQ",
                                  2: "MSQ",
                                  3: "NAT",
                                };
                                const [bg, col] = complexityStyle[
                                  q.complexityLevel
                                ] ?? ["#1e293b", "#94a3b8"];
                                const titleHtml = (q.title || [])
                                  .map((block) => {
                                    if (block.descType === "img") {
                                      return `<img src="${block.description}" alt="question image" style="max-width:100%;border-radius:4px;margin:6px 0;display:block;" />`;
                                    }
                                    return block.description ?? "";
                                  })
                                  .join("");

                                return (
                                  <div key={i} style={s.card}>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 6,
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        marginBottom: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#6366f1",
                                          fontWeight: 700,
                                        }}
                                      >
                                        Q{q.order}
                                      </span>
                                      {q.section?.sectionId && (
                                        <span
                                          style={s.badge("#1e3a5f", "#60a5fa")}
                                        >
                                          Sec {q.section.sectionId.slice(-4)}
                                        </span>
                                      )}
                                      <span style={s.badge(bg, col)}>
                                        {complexityLabel[q.complexityLevel] ??
                                          "—"}
                                      </span>
                                      {q.questionType && (
                                        <span
                                          style={s.badge("#1e3a5f", "#818cf8")}
                                        >
                                          {qtypeLabel[q.questionType] ??
                                            `Type ${q.questionType}`}
                                        </span>
                                      )}
                                      {q.unit && (
                                        <span
                                          style={s.badge("#1a2e1a", "#4ade80")}
                                        >
                                          {q.unit}
                                        </span>
                                      )}
                                      <span
                                        style={{
                                          marginLeft: "auto",
                                          fontSize: 11,
                                          color: "#475569",
                                        }}
                                      >
                                        +{q.marks ?? 0} / -
                                        {q.negativeMarks ?? 0}
                                      </span>
                                    </div>

                                    <div
                                      style={{
                                        fontSize: 13,
                                        color: "#e2e8f0",
                                        lineHeight: 1.6,
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: sanitize(titleHtml),
                                      }}
                                    />

                                    {q.questionType !== 3 && (
                                      <div style={{ marginTop: 8 }}>
                                        {q.options?.map((opt, j) => (
                                          <div
                                            key={j}
                                            style={s.opt(opt.isCorrect)}
                                          >
                                            <span
                                              style={{
                                                fontWeight: 700,
                                                color: opt.isCorrect
                                                  ? "#10b981"
                                                  : "#64748b",
                                                minWidth: 18,
                                                fontSize: 13,
                                              }}
                                            >
                                              {String.fromCharCode(65 + j)}.
                                            </span>
                                            <span
                                              style={{
                                                fontSize: 13,
                                                color: opt.isCorrect
                                                  ? "#a7f3d0"
                                                  : "#cbd5e1",
                                              }}
                                              dangerouslySetInnerHTML={{
                                                __html:
                                                  opt.descType === "img"
                                                    ? `<img src="${opt.description}" alt="option image" style="max-width:100%;border-radius:4px;margin:6px 0;display:block;" />`
                                                    : sanitize(opt.description),
                                              }}
                                            />
                                            {opt.isCorrect && (
                                              <span
                                                style={{
                                                  marginLeft: "auto",
                                                  color: "#10b981",
                                                }}
                                              >
                                                ✓
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {q.questionType === 3 && q.range && (
                                      <div
                                        style={{
                                          marginTop: 8,
                                          padding: "6px 10px",
                                          background: "#0f172a",
                                          borderRadius: 6,
                                          fontSize: 12,
                                          color: "#94a3b8",
                                        }}
                                      >
                                        Answer range:{" "}
                                        <strong style={{ color: "#f0abfc" }}>
                                          {q.range.r1}
                                        </strong>{" "}
                                        to{" "}
                                        <strong style={{ color: "#f0abfc" }}>
                                          {q.range.r2}
                                        </strong>
                                        {q.decimalPlaces > 0 && (
                                          <span
                                            style={{
                                              marginLeft: 8,
                                              color: "#64748b",
                                            }}
                                          >
                                            (up to {q.decimalPlaces} decimal
                                            places)
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {q.ansDescription?.text && (
                                      <details style={{ marginTop: 8 }}>
                                        <summary
                                          style={{
                                            cursor: "pointer",
                                            fontSize: 12,
                                            color: "#6366f1",
                                            fontWeight: 600,
                                          }}
                                        >
                                          📖 Explanation
                                        </summary>
                                        <div
                                          style={{
                                            marginTop: 6,
                                            padding: 10,
                                            background: "#0f172a",
                                            borderRadius: 6,
                                            fontSize: 12,
                                            color: "#94a3b8",
                                            lineHeight: 1.7,
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: sanitize(
                                              q.ansDescription.text,
                                            ),
                                          }}
                                        />
                                      </details>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* JSON tab */}
                          {activeTab === "json" && (
                            <div style={{ marginTop: 12, padding: "0 8px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 8,
                                }}
                              >
                                <span
                                  style={{ fontSize: 13, color: "#94a3b8" }}
                                >
                                  {activeQuestions.length} questions ·{" "}
                                  {activeItem.year}/{activeItem.month}
                                </span>
                                <button
                                  style={s.btn("#b91010", false)}
                                  onClick={() =>
                                    handleExport([
                                      {
                                        fileName: activeItem.file.name,
                                        questions: activeQuestions,
                                      },
                                    ])
                                  }
                                >
                                  ⬇️ Download PDF
                                </button>
                                <button
                                  style={s.btn("#10b981", false)}
                                  onClick={() => copyJSON(activeQuestions)}
                                >
                                  📋 Copy JSON
                                </button>
                              </div>
                              <div
                                style={{
                                  background: "#0f172a",
                                  border: "1px solid #334155",
                                  borderRadius: 8,
                                  padding: 14,
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  overflow: "auto",
                                  maxHeight: 480,
                                  whiteSpace: "pre-wrap",
                                  fontFamily: "monospace",
                                }}
                              >
                                {JSON.stringify(
                                  {
                                    totalExtracted: activeQuestions.length,
                                    year: activeItem.year,
                                    month: activeItem.month,
                                    questions: activeQuestions,
                                  },
                                  null,
                                  2,
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* Empty state */}
                      {fileQueue.length === 0 && (
                        <div
                          style={{
                            padding: 40,
                            textAlign: "center",
                            color: "#475569",
                          }}
                        >
                          <div style={{ fontSize: 40, marginBottom: 12 }}>
                            📂
                          </div>
                          <p style={{ fontSize: 14, margin: 0 }}>
                            Upload PYQ papers on the left to get started
                          </p>
                          <p
                            style={{
                              fontSize: 12,
                              marginTop: 6,
                              color: "#334155",
                            }}
                          >
                            Add year & month for each file, then extract all at
                            once
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Explicit save — extraction only fills the output panel
                      above; nothing is persisted until the admin reviews it
                      and clicks this. Sits outside the scrolling content so
                      it stays pinned to the bottom of the card. */}
                  {activeItem && activeQuestions.length > 0 && (
                    <div
                      style={{
                        padding: 16,
                        borderTop: "1px solid #e2e8f0",
                        background: "#fff",
                      }}
                    >
                      <button
                        style={{
                          width: "100%",
                          padding: "14px 20px",
                          borderRadius: 12,
                          border: "none",
                          cursor:
                            activeItem.saving || activeItem.saved
                              ? "not-allowed"
                              : "pointer",
                          fontWeight: 700,
                          fontSize: 15,
                          color: "#fff",
                          background:
                            activeItem.saved || activeItem.saving
                              ? "#94a3b8"
                              : "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
                          transition: "all 0.2s",
                        }}
                        disabled={activeItem.saving || activeItem.saved}
                        onClick={() => handleSaveItem(activeItem)}
                      >
                        {activeItem.saved
                          ? "✅ Saved"
                          : activeItem.saving
                            ? "Saving…"
                            : "⚡ Save Data"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}